/**
 * UserContext - Global state management for user data
 * Handles: authentication, profile, partners, preferences
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase, getAuthToken } from '../config/supabase';
import { API_BASE_URL } from '../config/api';
import { apiFetch, authHeaders } from '../services/apiClient';
import { normalizePartner, normalizeProfile } from '../services/profileAdapter';

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [partners, setPartners] = useState([]);
  const [userChildren, setUserChildren] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Load all user data from API (using provided token)
   */
  const loadUserDataWithToken = useCallback(async (userId, token) => {
    console.log("[UserContext] loadUserDataWithToken called for userId:", userId);
    if (!userId || !token) {
      console.warn("[UserContext] loadUserDataWithToken: Missing userId or token!");
      return;
    }

    try {
      console.log("[UserContext] Fetching profile from API...");
      const profileData = await apiFetch('/api/profiles/me/full', {
        headers: authHeaders(token)
      });

      setProfile(normalizeProfile(profileData.profile || {}));
      setPartners((profileData.partners || []).map(normalizePartner));
      setUserChildren(profileData.children || []);
      setPreferences(profileData.preferences);
      console.log("[UserContext] Profile loaded successfully");

    } catch (err) {
      if (err.status === 401) {
        console.warn("Token expired, signing out...");
        await supabase.auth.signOut();
        setUser(null);
        setProfile(null);
        return;
      }

      console.error('Error loading user data:', err);
      setError(err.message);
    }
  }, []);

  /**
   * Load all user data from API (gets token automatically)
   */
  const loadUserData = useCallback(async (userId) => {
    console.log("[UserContext] loadUserData called with userId:", userId);
    const token = await getAuthToken();
    if (!token) {
      console.error("[UserContext] No auth token available!");
      throw new Error("No auth token available");
    }
    await loadUserDataWithToken(userId, token);
  }, [loadUserDataWithToken]);

  // Initialize auth listener
  useEffect(() => {
    let mounted = true;
    console.log("[UserContext] useEffect mounted");

    // Simple auth initialization - no complex timeouts
    const initAuth = async () => {
      console.log("[UserContext] initAuth started");
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error("[UserContext] getSession error:", error);
          throw error;
        }

        console.log("[UserContext] getSession result:", session ? `Session found for ${session.user?.email}` : "No session");

        if (mounted) {
          setUser(session?.user ?? null);
          if (session?.user && session.access_token) {
            // Pass the token directly - don't call getSession again
            await loadUserDataWithToken(session.user.id, session.access_token);
          }
          setLoading(false);
        }
      } catch (err) {
        console.error("[UserContext] Auth Init Error:", err);
        if (mounted) {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("[UserContext] Auth State Change:", event, session?.user?.email);

      if (mounted) {
        setUser(session?.user ?? null);

        if (session?.user) {
          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            console.log("[UserContext] Loading user data after", event);
            try {
              await loadUserDataWithToken(session.user.id, session.access_token);
            } catch (err) {
              console.error("[UserContext] loadUserData failed:", err);
            }
          }
          setLoading(false);
        } else {
          setProfile(null);
          setPartners([]);
          setUserChildren([]);
          setPreferences(null);
          setLoading(false);
        }
      }
    });

    return () => {
      console.log("[UserContext] useEffect cleanup");
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [loadUserData, loadUserDataWithToken]);

  /**
   * Sign up new user
   */
  const signup = useCallback(async (userData) => {
    try {
      setError(null);
      // Normalize dateOfBirth to YYYY-MM-DD for FastAPI/Pydantic
      const dobRaw = userData.dateOfBirth;
      let dobIso = dobRaw;
      try {
        // Accept MM/DD/YYYY or YYYY-MM-DD
        const parsed = new Date(dobRaw);
        if (!isNaN(parsed.getTime())) {
          dobIso = parsed.toISOString().slice(0, 10);
        }
      } catch (_) { /* keep original if parsing fails */ }

      const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userData.email,
          password: userData.password,
          firstName: userData.firstName,
          lastName: userData.lastName,
          dateOfBirth: dobIso,
          relationshipStatus: userData.relationshipStatus
        })
      });

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Backend server is not responding correctly. Please ensure the API server is running at ' + API_BASE_URL);
      }

      const data = await response.json();
      if (!response.ok) {
        const detail = (data && (data.detail ?? data.error ?? data.message)) ?? 'Signup failed';
        const msg = typeof detail === 'string' ? detail : JSON.stringify(detail);
        throw new Error(msg);
      }

      // Establish a Supabase session so the UI knows the user is signed in
      const { error: supabaseError } = await supabase.auth.signInWithPassword({
        email: userData.email,
        password: userData.password
      });

      if (supabaseError) {
        throw new Error(supabaseError.message || 'Signup succeeded but automatic login failed');
      }

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Sign in user
   */
  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      if (!response.ok) {
        const detail = (data && (data.detail ?? data.error ?? data.message)) ?? 'Login failed';
        const msg = typeof detail === 'string' ? detail : JSON.stringify(detail);
        throw new Error(msg);
      }

      // Ensure the Supabase JS client has a session for downstream requests
      const { error: supabaseError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (supabaseError) {
        throw new Error(supabaseError.message || 'Login succeeded but session could not be established');
      }

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Sign out user
   */
  const logout = useCallback(async () => {
    try {
      setError(null);
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);
      setPartners([]);
      setUserChildren([]);
      setPreferences(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Mark onboarding as complete
   */
  const completeOnboarding = useCallback(async () => {
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('No authentication token available');

      const response = await fetch(`${API_BASE_URL}/api/profiles/me/onboarding-complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to mark onboarding complete');

      await loadUserData(user.id);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [user, loadUserData]);

  /**
   * Update user profile
   */
  const updateProfile = useCallback(async (updates) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('No authentication token available. Please log in again.');
      }

      const response = await fetch(`${API_BASE_URL}/api/profiles/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.detail || 'Failed to update profile');

      setProfile(normalizeProfile(data.profile));
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Add partner
   */
  const addPartner = useCallback(async (partnerData) => {
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('No authentication token available');

      const response = await fetch(`${API_BASE_URL}/api/partners`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(partnerData)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to add partner');

      setPartners([...partners, normalizePartner(data.partner)]);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [partners]);

  /**
   * Update partner
   */
  const updatePartner = useCallback(async (partnerId, updates) => {
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('No authentication token available');

      const response = await fetch(`${API_BASE_URL}/api/partners/${partnerId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.detail || 'Failed to update partner');

      setPartners(partners.map(p => p.id === partnerId ? normalizePartner(data.partner) : p));
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [partners]);

  /**
   * Delete partner
   */
  const deletePartner = useCallback(async (partnerId) => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/partners/${partnerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete partner');

      setPartners(partners.filter(p => p.id !== partnerId));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [partners]);

  /**
   * Add child
   */
  const addChild = useCallback(async (childData) => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/children`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(childData)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to add child');

      setUserChildren([...userChildren, data.child]);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [userChildren]);

  /**
   * Delete child
   */
  const deleteChild = useCallback(async (childId) => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/children/${childId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete child');

      setUserChildren(userChildren.filter(c => c.id !== childId));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [userChildren]);

  /**
   * Update preferences (auto-save)
   */
  const updatePreferences = useCallback(async (updates) => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.detail || 'Failed to update preferences');

      setPreferences(data.preferences);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const value = {
    // State
    user,
    profile,
    partners,
    children: userChildren,
    preferences,
    loading,
    error,

    // Auth methods
    signup,
    login,
    logout,

    // Profile methods
    completeOnboarding,
    updateProfile,

    // Partner methods
    addPartner,
    updatePartner,
    deletePartner,

    // Children methods
    addChild,
    deleteChild,

    // Preferences methods
    updatePreferences,

    // Data loading
    loadUserData
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

/**
 * Hook to use UserContext
 */
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

export default UserContext;
